import SubmitContext from 'context/submit/submitContext'
import OatsOatmealProduct from 'models/OatsOatmealProduct'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import { Col, FormGroup, Input, Row, Alert } from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  oatTypeOptions,
  cookingTypeOptions,
  varietyOptions,
  packagingInitialState,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const BSOatsOatmealPackage = () => {
  const submitContext = useContext(SubmitContext)

  const { current, setCurrent, updating } = submitContext

  // eslint-disable-next-line no-unused-vars
  const [packaging, setPackaging] = useState(packagingInitialState)

  const { onChange, setSelect, asterisk } = useFormUtils()

  const methods = useFormContext()

  useEffect(() => {
    if (
      current?.category === packagingInitialState.category &&
      current?.subCategory === packagingInitialState.subCategory
    ) {
      const {
        category,
        subCategory,
        GrainSize,
        OatType,
        CookingType,
        Variety,
        OtherType,
        OtherVariety,
      } = current
      setPackaging({
        category,
        subCategory,
        GrainSize,
        OatType: {
          value: OatType || '',
          label: OatType.length > 0 ? OatType : 'Oat Type',
        },
        CookingType: {
          value: CookingType || '',
          label: CookingType.length > 0 ? CookingType : 'Oat Cooking Type',
        },
        Variety: {
          value: Variety || '',
          label: Variety.length > 0 ? Variety : 'Oat Variety',
        },
        OtherType,
        OtherVariety,
      })
    } else if (!updating) {
      setCurrent(OatsOatmealProduct)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  useEffect(() => {
    if (updating) {
      if (current?.Variety === 'Other') {
        methods.setValue('OtherVariety', current.OtherVariety)
      }
      if (current?.OatType === 'Other') {
        methods.setValue('OtherType', current.OtherType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={6}>
        <p>
          <strong>Oat Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={oatTypeOptions}
          name="OatType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Oat Type'}
          onChange={([selected]) => {
            setSelect(selected, 'OatType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="OatType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        {current?.OatType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherType"
              placeholder={'Other Type'}
              defaultValue={current?.OtherType}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Type exceeds 15 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="OtherType"
                message={'Other type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
          <Controller
            as={Input}
            type="number"
            step="0.01"
            name="Size"
            defaultValue={packaging.Size}
            placeholder={'12 - 48 ounces'}
            onKeyDown={e => {
              if (isNotNumericDecimal(e.key)) {
                e.preventDefault()
              }
            }}
            control={methods.control}
            rules={{
              maxLength: {
                value: 5,
                message: '⚠ Size exceeds 5 characters.',
              },
              min: {
                value: 12,
                message: '⚠ Size is below the required 12 ounce minimum.',
              },
              max: {
                value: 48,
                message: '⚠ Size exceeds the 48 ounces allowed.',
              },
              required: {
                value: true,
                message: '⚠ Please enter product size.',
              },
              pattern: {
                value: /^\d+(\.\d{1,2})?$/,
                message: '⚠ Maximum two decimal places allowed.',
              },
            }}
            onChange={([event]) => {
              onChange(event)
              return event
            }}
          />
          <p className={'text-danger'}>
            <ErrorMessage
              errors={methods.errors}
              name="Size"
              as={<Alert color={'danger'} className={'mt-3'} />}
            />
          </p>
        </FormGroup>
      </Col>
      <Col xs={6}>
        <p>
          <strong>Variety {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={varietyOptions}
          name="Variety"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Oat Variety'}
          onChange={([selected]) => {
            setSelect(selected, 'Variety')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="Variety"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        {current?.Variety === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Variety (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="OtherVariety"
              placeholder={'Other Variety'}
              defaultValue={current?.OtherVariety}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Variety exceeds 15 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key) && e.key !== ' ') {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="OtherVariety"
                message={'Other variety is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <p>
          <strong>Cooking Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={cookingTypeOptions}
          name="CookingType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Oat Cooking Type'}
          onChange={([selected]) => {
            setSelect(selected, 'CookingType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="CookingType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

export default BSOatsOatmealPackage
